/**
 * Password strength and confirmation hints.
 *
 * The rules mirror the server side validation so that the visitor learns
 * about a weak password before submitting the form.
 */

import { text } from "../core/i18n";
import { query, queryAll } from "../core/dom";

export interface PasswordRules {
    readonly length: boolean;
    readonly upper: boolean;
    readonly lower: boolean;
    readonly digit: boolean;
}

export const MINIMUM_LENGTH = 8;

/** Return which of the password rules `value` satisfies. */
export function checkPassword(value: string): PasswordRules {
    return {
        length: value.length >= MINIMUM_LENGTH,
        upper: /[A-Z]/.test(value),
        lower: /[a-z]/.test(value),
        digit: /[0-9]/.test(value),
    };
}

/** Return how many rules out of four are satisfied. */
export function passwordScore(value: string): number {
    const rules = checkPassword(value);

    return [rules.length, rules.upper, rules.lower, rules.digit].filter(Boolean).length;
}

/** Return the hint shown below a password field. */
export function passwordHint(value: string): string {
    if (value.length === 0) {
        return text("js.password.rules", "Minimálne 8 znakov, jedno veľké písmeno, jedno malé písmeno a jedna číslica.");
    }

    const rules = checkPassword(value);
    const missing: string[] = [];

    if (!rules.length) {
        missing.push(text("js.password.missing_length", "aspoň {count} znakov", { count: MINIMUM_LENGTH }));
    }

    if (!rules.upper) {
        missing.push(text("js.password.missing_upper", "veľké písmeno"));
    }

    if (!rules.lower) {
        missing.push(text("js.password.missing_lower", "malé písmeno"));
    }

    if (!rules.digit) {
        missing.push(text("js.password.missing_digit", "číslicu"));
    }

    if (missing.length === 0) {
        return text("js.password.ok", "Heslo spĺňa všetky požiadavky.");
    }

    return text("js.password.missing", "Heslu chýba: {list}.", { list: missing.join(", ") });
}

/** How a password of each score is spoken of. */
const STRENGTH = ["weak", "weak", "fair", "good", "strong"] as const;

/** Return the word for a password satisfying `score` of the four rules. */
export function strengthName(score: number): string {
    return STRENGTH[Math.min(Math.max(score, 0), 4)] ?? "weak";
}

/**
 * Draw the meter of one password field.
 *
 * The meter is the same four rules the hint lists, counted rather than named:
 * the sentence says what is missing, the bar says how far there is to go. An
 * empty field shows nothing at all - a password nobody has started typing is
 * not a weak one.
 */
export function drawMeter(meter: HTMLElement, value: string): void {
    const score = value.length === 0 ? 0 : passwordScore(value);
    const name = strengthName(score);
    const bar = query<HTMLElement>("[data-password-bar]", meter);
    const label = query<HTMLElement>("[data-password-level]", meter);

    meter.dataset["level"] = value.length === 0 ? "" : name;

    if (bar) {
        bar.style.width = `${(score / 4) * 100}%`;
    }

    if (label) {
        label.textContent =
            value.length === 0
                ? ""
                : text(`js.password.level_${name}`, name === "strong" ? "Silné heslo" : "Slabé heslo");
    }
}

/** Wire the strength hint and the confirmation check of the password fields. */
export function initPasswordFields(): void {
    for (const field of queryAll<HTMLInputElement>("[data-password-strength]")) {
        const parent = field.parentElement;
        const hint = parent ? query<HTMLElement>("[data-password-hint]", parent) : null;
        const meter = parent ? query<HTMLElement>("[data-password-meter]", parent) : null;

        if (!hint && !meter) {
            continue;
        }

        const update = (): void => {
            if (hint) {
                hint.textContent = passwordHint(field.value);
                hint.classList.toggle("is-invalid", passwordScore(field.value) < 4 && field.value.length > 0);
            }

            if (meter) {
                drawMeter(meter, field.value);
            }
        };

        field.addEventListener("input", update);
        update();
    }

    for (const confirmation of queryAll<HTMLInputElement>("[data-password-confirm]")) {
        const originalId = confirmation.dataset["passwordConfirm"] ?? "";
        const original = document.getElementById(originalId);
        const output = confirmation.parentElement
            ? query<HTMLElement>("[data-password-match]", confirmation.parentElement)
            : null;

        if (!(original instanceof HTMLInputElement) || !output) {
            continue;
        }

        const update = (): void => {
            if (confirmation.value.length === 0) {
                output.textContent = "";
                confirmation.setCustomValidity("");

                return;
            }

            const same = confirmation.value === original.value;
            output.textContent = same
                ? text("js.password.match", "Heslá sa zhodujú.")
                : text("js.password.mismatch", "Heslá sa nezhodujú.");
            output.classList.toggle("is-invalid", !same);
            confirmation.setCustomValidity(same ? "" : text("js.password.mismatch", "Heslá sa nezhodujú."));
        };

        confirmation.addEventListener("input", update);
        original.addEventListener("input", update);
    }
}
